import { Link } from 'react-router-dom';
import classNames from 'classnames';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import Sticky from 'react-stickynode';

import { Tab } from '../../entities/tab';
import { strings, getLocale } from '../../localization/strings';
import CommonButtonDefault from '../common/buttons/default';
import * as ordersApi from '../../services/api/orders';
import AlertPrimary from '../common/alerts/alert-primary';
import { useAuth } from '../../hooks/use-auth';

interface ComponentProps {
  tabs: Tab[];
  pdfId?: string | null;
  orderHash?: string | null;
}

const POLL_INTERVAL = 5000;
const POLL_TIMEOUT = 120000;

function OrderTabs({ tabs, pdfId, orderHash }: ComponentProps) {
  const [error, setError] = useState<string>('');
  const { token, tokenAdmin } = useAuth();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [localPdfId, setLocalPdfId] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const effectivePdfId = localPdfId || pdfId;

  const downloadPdf = async () => {
    if (effectivePdfId) {
      try {
        setDisabled(true);
        const res = await ordersApi.getPdf(token || tokenAdmin, effectivePdfId);

        if (res) {
          setDisabled(false);
          window.location.href = res;
        }
      } catch (e) {
        setDisabled(false);
        setError((e as Error).message);
      }
    }
  };

  const generatePdf = async () => {
    if (!orderHash) return;

    try {
      setGenerating(true);
      setError('');
      const filename = await ordersApi.generateResultPdf(token || tokenAdmin, orderHash, getLocale());

      const startTime = Date.now();

      pollTimerRef.current = setInterval(async () => {
        if (Date.now() - startTime > POLL_TIMEOUT) {
          stopPolling();
          setGenerating(false);
          setError('PDF generation timed out. Please try again.');

          return;
        }

        try {
          const downloadUrl = await ordersApi.getPdf(token || tokenAdmin, filename);

          if (downloadUrl) {
            stopPolling();
            setGenerating(false);
            setLocalPdfId(filename);
            window.location.href = downloadUrl;
          }
        } catch {
          // PDF not ready yet, continue polling
        }
      }, POLL_INTERVAL);
    } catch (e) {
      setGenerating(false);
      setError((e as Error).message);
    }
  };

  const showDownload = !!effectivePdfId;
  const showGenerate = !effectivePdfId && !!orderHash;

  return (
    <>
      <Sticky innerZ={5} className="print:!hidden">
        <div className="print:border-0 border-b bg-white border-gray-200 px-2 md:px-0">
          <div className="container max-w-4xl mx-auto">
            <div className="flex justify-center relative">
              <nav className="-mb-px flex space-x-2 md:space-x-8" aria-label="Tabs">
                {tabs.map((tab) => (
                  <Link
                    key={tab.name}
                    to={tab.href}
                    className={classNames(
                      tab.current
                        ? 'border-blue-800 text-blue-800'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                      'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm no-underline',
                    )}
                    aria-current={tab.current ? 'page' : undefined}
                  >
                    {tab.name}
                  </Link>
                ))}
              </nav>
              {showDownload && (
                <div className="flex items-center absolute right-0 top-0 bottom-0">
                  <CommonButtonDefault disabled={disabled} sm primary onClick={downloadPdf}>
                    {strings().button.pdf}
                  </CommonButtonDefault>
                </div>
              )}
              {showGenerate && (
                <div className="flex items-center absolute right-0 top-0 bottom-0">
                  <CommonButtonDefault disabled={generating} sm primary onClick={generatePdf}>
                    <span className="flex items-center gap-2">
                      {generating && (
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      )}
                      {generating ? strings().button.generatingPdf : strings().button.generatePdf}
                    </span>
                  </CommonButtonDefault>
                </div>
              )}
            </div>
          </div>
        </div>
      </Sticky>
      {generating && (
        <div className="container max-w-4xl mx-auto mt-3">
          <AlertPrimary title={strings().button.generatingPdfInfo} />
        </div>
      )}
      {error && (
        <div className="container max-w-4xl mx-auto mt-3">
          <AlertPrimary type="danger" title={error} />
        </div>
      )}
    </>
  );
}

OrderTabs.defaultProps = {
  pdfId: null,
  orderHash: null,
};

export default OrderTabs;
